import React, { useMemo } from "react";
import { Form, Checkbox } from "antd";
import RoleFormItems from "../fields";
import { roleUseMutation } from "../../hooks";
import { Localize, EntityRemoveButton } from "components/service";
import { localizeByKey } from "components/service/localize";
import Icons from "components/icons";
import Variable from "components/variable";
import { TRole } from "components/role/helpers/role-types-helper";
import { useQuery } from "@apollo/client";
import { GET_PERMISSIONS } from "graphql/query/permission-gql";
import { PermissionButton, useModelFormValidation } from "components/permissions";
import RoleConst from "components/role/role-const";
import "./role-edit-form.scss";

interface IRoleEditFormProps
{
  role: TRole | undefined;
}

const RoleEditForm: React.FC<IRoleEditFormProps> = ({ role }) =>
{
  const [ form ] = Form.useForm();

  const { validateUpdatePermission, validateDeletePermission } = useModelFormValidation(RoleConst);

  const permissionIds: number[] = [];
  role?.permissions?.map((perm) => permissionIds.push(parseInt(perm.id)));

  const { _setRoleCreateUpdate, loadingMutationRoleCreateUpdate } =
    roleUseMutation.createUpdate(role?.id);

  const { _setMutationRoleDelete, loadingMutationRoleDelete } =
    roleUseMutation.delete(role?.id);

  const { data: permissionsData, loading: loadingPermissions } = useQuery(GET_PERMISSIONS, {
    variables: { first: 200 },
    fetchPolicy: "cache-first"
  });

  const groupedPermissions = useMemo(() =>
  {
    if (!permissionsData?.permissions?.data) return { library: [], app: [] };
    const library: any[] = [];
    const app: any[] = [];
    const other: any[] = [];

    permissionsData.permissions.data.forEach((perm: any) =>
    {
      if (perm.entry_point === 'library') {
        library.push(perm);
      } else if (perm.entry_point === 'app') {
        app.push(perm);
      } else {
        other.push(perm);
      }
    });


    return { library, app, other };
  }, [ permissionsData ]);

  const groupPermissionsByCategory = (permissions: any[]) =>
  {
    const groups: Record<string, any[]> = {};
    permissions.forEach((perm) =>
    {
      const category = perm.group_name || 'Other';
      if (!groups[ category ]) groups[ category ] = [];
      groups[ category ].push(perm);
    });
    return groups;
  };

  // Order of permissions groups
  const libraryPermissionOrder = [
    'permission_group_special',
    'permission_group_users',
    'permission_group_user_roles',
    'permission_group_suppliers',
    'permission_group_organizers',
    'permission_group_exhibition_types',
    'permission_group_stand_types',
    'permission_group_equipments',
    'permission_group_floorings',
    'permission_group_teams',
    'permission_group_variable_lists',
    'permission_group_graphic_rules'
  ];

  const sortPermissionsByOrder = (permissions: any[]) => {
    const grouped = groupPermissionsByCategory(permissions);
    const sortedGroups: Record<string, any[]> = {};

    libraryPermissionOrder.forEach(groupName => {
      if (grouped[groupName]) {
        sortedGroups[groupName] = grouped[groupName];
      }
    });

    // Add other groups at the end
    Object.keys(grouped).forEach(groupName => {
      if (!libraryPermissionOrder.includes(groupName)) {
        sortedGroups[groupName] = grouped[groupName];
      }
    });

    return sortedGroups;
  };

  const isDeveloperRole = role?.slug === 'role_developer';

  const onFinish = async (values: any) => {
    if (!validateUpdatePermission()) {
      return;
    }

    await _setRoleCreateUpdate({
      variables: {
        input: {
          id: role?.id ? +role.id : undefined,
          ...values,
        },
      },
    });
  };

  return (
    <Form
      key="edit-role-form"
      layout="vertical"
      form={form}
      className="model-form edit-role-form"
      initialValues={{ permission_id: permissionIds }}
      onFinish={onFinish}
    >
      <div
        className="row-grid grid-gap-10 grid-tablet"
        style={{ gridTemplateColumns: "1fr 220px" }}
      >
        <Variable.Forms.Fields.VariablesSelect
          form={form}
          name="title"
          groupName="role"
          excludeSlugs={ role?.id ? undefined : ['role_developer'] }
          initialValue={role?.title}
          label={localizeByKey("FORMS.Input_Label_RoleName")}
        />
        <RoleFormItems.ColorSelect initialValue={role?.colorHex} />
      </div>
      <br />

      <Form.Item name="permission_id" className="form-item-checkbox">
        <Checkbox.Group >
          <h5 className="form-heading">
            <Localize>FORMS.Headig_Text_AppRights</Localize>
          </h5>
          <div className="form-checkbox-holder row-grid grid-gap-10 col-2 col-md-3">
            {loadingPermissions ? (
              <div>Loading...</div>
            ) : (
              Object.entries(sortPermissionsByOrder(groupedPermissions.app)).map(([ group, perms ]) => (
                <div className="col" key={group}>
                  <div className="heading">
                    <h6><Localize>{group}</Localize></h6>
                  </div>
                  {perms.map((perm) => (
                    <div key={perm.id} className="permission-item">
                      {isDeveloperRole ? (
                        <span className="permission-name">
                          <Localize>{perm.slug}</Localize>
                        </span>
                      ) : (
                        <Checkbox value={parseInt(perm.id)}>
                          <Localize>{perm.slug}</Localize>
                        </Checkbox>
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>

          <h5 className="form-heading">
            <Localize>FORMS.Headig_Text_LibraryRights</Localize>
          </h5>
          <div className="form-checkbox-holder row-grid grid-gap-10 col-2 col-md-3">
            {loadingPermissions ? (
              <div>Loading...</div>
            ) : (
              Object.entries(sortPermissionsByOrder(groupedPermissions.library)).map(([ group, perms ]) => (
                <div className="col" key={group}>
                  <div className="heading">
                    <h6><Localize>{group}</Localize></h6>
                  </div>
                  {perms.map((perm) => (
                    <div key={perm.id} className="permission-item">
                      {isDeveloperRole ? (
                        <span className="permission-name">
                          <Localize>{perm.slug}</Localize>
                        </span>
                      ) : (
                        <Checkbox value={parseInt(perm.id)}>
                          <Localize>{perm.slug}</Localize>
                        </Checkbox>
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </Checkbox.Group>
      </Form.Item>

      {!isDeveloperRole && (
        <div className="form-btn-holder">
          {role?.id && (
            <EntityRemoveButton
              modalButton={
                <PermissionButton
                  permission={RoleConst.permissions.DELETE}
                  disabledFallback={true}
                  icon={<Icons.Delete />}
                >
                  <span className="ellipsis">
                    <Localize>ROLE.Button_Text_Delete</Localize>
                  </span>
                </PermissionButton>
              }
              nameEntity={localizeByKey("ENTITY.Modal_Title_Role")}
              dataNameEntity={`${role?.title}`}
              loading={loadingMutationRoleDelete}
              deleteMutation={(options) =>
              {
                if (!validateDeletePermission()) {
                  return Promise.reject(new Error('Access denied'));
                }
                return _setMutationRoleDelete(options);
              }}
              variables={{
                id: role?.id,
              }}
            />
          )}
          <PermissionButton
            permission={RoleConst.permissions.UPDATE}
            disabledFallback={true}
            htmlType="submit"
            className="btn-right"
            type="primary"
            icon={role?.id ? <Icons.Edit /> : <Icons.Plus />}
            loading={loadingMutationRoleCreateUpdate}
          >
            <span className="ellipsis">
              {role?.id ? (
                <Localize>GLOBAL.Button_Text_SaveChanges</Localize>
              ) : (
                <Localize>GLOBAL.Button_Text_CreateNew</Localize>
              )}
            </span>
          </PermissionButton>
        </div>
      )}
    </Form>
  );
};

export default RoleEditForm;